import { DocumentationApiError } from "./models";
import type { DocumentationDraft, DocumentationDraftStatus, DocumentationGroupResponse, DocumentationProgressEvent } from "./models";

export const DOCUMENTATION_PROCESS_POLL_INTERVAL_MS = 1_500;
export const DOCUMENTATION_PROCESS_TIMEOUT_MS = 5 * 60 * 1_000;

const ACTIVE_PROCESS_STATUSES: ReadonlySet<DocumentationDraftStatus> = new Set(["created", "processing", "finalizing"]);

export function isDocumentationDraftProcessing(status: DocumentationDraftStatus): boolean {
    return ACTIVE_PROCESS_STATUSES.has(status);
}

export function shouldPollDocumentationDraft(events: DocumentationProgressEvent[], latestDraft: DocumentationDraft | undefined): boolean {
    return events.some(event => event.event === "processing_pending") || !latestDraft || isDocumentationDraftProcessing(latestDraft.status);
}

type PollDocumentationDraftOptions = {
    draftId: string;
    loadGroup: (signal: AbortSignal) => Promise<DocumentationGroupResponse>;
    onGroupLoaded: (response: DocumentationGroupResponse) => void;
    signal?: AbortSignal;
    intervalMs?: number;
    timeoutMs?: number;
    now?: () => number;
    delay?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
};

function abortError(signal: AbortSignal): unknown {
    return signal.reason ?? new DOMException("The documentation operation was cancelled.", "AbortError");
}

const wait = (milliseconds: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(abortError(signal));
            return;
        }

        const timer = globalThis.setTimeout(() => {
            signal.removeEventListener("abort", handleAbort);
            resolve();
        }, milliseconds);
        const handleAbort = () => {
            globalThis.clearTimeout(timer);
            reject(abortError(signal));
        };
        signal.addEventListener("abort", handleAbort, { once: true });
    });

/**
 * A process request may be an idempotent replay while another request still owns
 * the processing lease. In that case its HTTP response is not the completion
 * signal; the persisted Draft status is. Poll until that Draft reaches any
 * non-active (success or failure) state.
 */
export async function pollDocumentationDraftUntilTerminal({
    draftId,
    loadGroup,
    onGroupLoaded,
    signal,
    intervalMs = DOCUMENTATION_PROCESS_POLL_INTERVAL_MS,
    timeoutMs = DOCUMENTATION_PROCESS_TIMEOUT_MS,
    now = Date.now,
    delay = wait
}: PollDocumentationDraftOptions): Promise<DocumentationDraft> {
    const startedAt = now();
    const pollController = new AbortController();
    const timeoutReason = new Error("Documentation processing polling timed out.");
    const forwardAbort = () => pollController.abort(signal?.reason);
    if (signal?.aborted) forwardAbort();
    else signal?.addEventListener("abort", forwardAbort, { once: true });
    const timeout = globalThis.setTimeout(() => pollController.abort(timeoutReason), timeoutMs);

    try {
        while (true) {
            if (pollController.signal.aborted) throw abortError(pollController.signal);
            const response = await loadGroup(pollController.signal);
            if (pollController.signal.aborted) throw abortError(pollController.signal);
            onGroupLoaded(response);

            const draft = response.drafts.find(item => item.id === draftId);
            if (!draft) {
                throw new DocumentationApiError("DOCUMENTATION_DRAFT_NOT_FOUND", "The documentation Draft disappeared while processing.", 404);
            }
            if (!isDocumentationDraftProcessing(draft.status)) return draft;

            const elapsedMs = now() - startedAt;
            if (elapsedMs >= timeoutMs) throw timeoutReason;
            await delay(Math.min(intervalMs, timeoutMs - elapsedMs), pollController.signal);
        }
    } catch (error) {
        if (error === timeoutReason || pollController.signal.reason === timeoutReason) {
            throw new DocumentationApiError("DOCUMENTATION_PROCESS_TIMEOUT", "The documentation is still processing. Please try again.", 504);
        }
        throw error;
    } finally {
        globalThis.clearTimeout(timeout);
        signal?.removeEventListener("abort", forwardAbort);
    }
}
